import { useMemo } from 'react'
import { Outlet, Link } from 'react-router-dom'
import { LoadingIndicator } from '@common/components/LoadingIndicator'
import { useAbility } from '@common/abilities/AbilityContext'
import { Bar } from 'react-chartjs-2'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip as ChartTooltip,
} from 'chart.js'
import {
    useActiveTransaction,
    useAgingTransaction,
    useAgingTransaction120,
    useProvisionalCreditRequired,
    useDisputeTeamPerformance,
} from './hooks/useDashboardData'

// Register Chart.js components (required for tree-shaking)
ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip)

/**
 * Dashboard module pages — replaces AngularJS controllers & templates.
 *
 * Sources:
 *   disputes.tpl.html + activeTransaction.tpl.html + agingTransaction.tpl.html
 *   agingTransaction120.tpl.html + provisionalCreditRequired.tpl.html
 *   disputeTeamPerformance.tpl.html
 *
 * Routes:
 *   /dashboard                         → DashboardLayout (abstract, renders <Outlet>)
 *   /dashboard/disputes                → DisputesDashboardPage (4 quadrant cards)
 *   /dashboard/dispute-team-performance → DisputeTeamPerformancePage
 */

// ========== Layout ==========
export function DashboardLayout() {
    return <Outlet />
}

// ========== Active Transaction Card ==========
/** Converted from activeTransaction.tpl.html + activeTransactionController.js */
function ActiveTransactionCard() {
    const { data, isLoading } = useActiveTransaction()

    if (isLoading) return <div className="card-loading">Loading...</div>
    if (!data) return null

    return (
        <>
            <div className="card-hd">
                <div className="card-title">Transaction Data</div>
            </div>
            <section className="card-section">
                <div className="dashboard-info-container">
                    <div className="dashboard-info-group dashboard-info-center">
                        <div className="dashboard-info-data">{data.totalActive?.toLocaleString()}</div>
                        <div className="dashboard-info-title">Total Active</div>
                    </div>
                    <div className="dashboard-info-group dashboard-info-center">
                        <div className="dashboard-info-data">
                            {data.totalAmount != null
                                ? `$${data.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : '—'}
                        </div>
                        <div className="dashboard-info-title">Total $ Amount</div>
                    </div>
                </div>
            </section>
            {data.businessLines && data.businessLines.length > 0 && (
                <ActiveTransactionChart businessLines={data.businessLines} />
            )}
        </>
    )
}

// ========== Active Transaction Chart ==========
/** Horizontal bar chart — converted from activeTransaction.tpl.html canvas element */
function ActiveTransactionChart({
    businessLines,
}: {
    businessLines: Array<{
        name: string
        totalCases?: number
        totalTrans?: number
        totalAmount?: number
    }>
}) {
    const chartData = useMemo(
        () => ({
            labels: businessLines.map((bl) => bl.name),
            datasets: [
                {
                    data: businessLines.map((bl) => bl.totalTrans ?? 0),
                    backgroundColor: '#00afd2',
                    borderWidth: 0,
                },
            ],
        }),
        [businessLines],
    )

    const chartOptions = useMemo(
        () => ({
            indexAxis: 'y' as const,
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                tooltip: {
                    displayColors: false,
                    padding: 15,
                    callbacks: {
                        label: (context: { dataIndex: number }) => {
                            const bl = businessLines[context.dataIndex]
                            const amt = bl.totalAmount ?? 0
                            const fmtAmt = Math.abs(amt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            const amtStr = amt < 0 ? `($${fmtAmt})` : `$${fmtAmt}`
                            return [
                                `# of Cases: ${bl.totalCases?.toLocaleString() ?? 0}`,
                                `# of Trans: ${bl.totalTrans?.toLocaleString() ?? 0}`,
                                `Total Amount: ${amtStr}`,
                            ]
                        },
                    },
                },
                legend: { display: false },
            },
            scales: {
                x: {
                    display: true,
                    ticks: {
                        callback: function(value: string | number) {
                            return String(value);
                        },
                    },
                },
                y: { display: true },
            },
        }),
        [businessLines],
    )

    // Match Angular: height scales with number of business lines, width=450
    const chartHeight = Math.max(180, businessLines.length * 18)

    return (
        <section className="card-section">
            <div className="chart-horizontal-bar" style={{ width: 450, height: chartHeight }}>
                <Bar data={chartData} options={chartOptions} />
            </div>
        </section>
    )
}

// ========== Aging Transaction Card (45/90) ==========
/** Converted from agingTransaction.tpl.html + agingTransactionController.js */
function AgingTransactionCard() {
    const { report, isLoading } = useAgingTransaction()

    if (isLoading) return <div className="card-loading">Loading...</div>

    const r = report as {
        total?: number; businessLines?: Array<{
            name: string; id?: number
            totalInFirstDateRangeForATM?: number; totalInSecondDateRangeForATM?: number; totalInThirdDateRangeForATM?: number
            totalInFirstDateRangeForPOS?: number; totalInSecondDateRangeForPOS?: number; totalInThirdDateRangeForPOS?: number
        }>
    } | null

    return (
        <>
            <div className="card-hd">
                <div className="card-title">Aging Transactions (45/90)</div>
            </div>
            {r && (
                <>
                    <section className="card-section">
                        <div className="dashboard-info-group dashboard-info-center">
                            <div className="dashboard-info-data">{r.total}</div>
                            <div className="dashboard-info-title">Total Transactions</div>
                        </div>
                    </section>
                    <table className="table table-sm table-align-right table-valign-middle">
                        <thead>
                            <tr className="primary-header">
                                <th></th>
                                <th colSpan={3}>ATM days</th>
                                <th colSpan={3}>POS days</th>
                            </tr>
                            <tr className="secondary-header">
                                <th></th>
                                <th>&lt; 38</th>
                                <th>38-45</th>
                                <th>Over 45</th>
                                <th>&lt; 81</th>
                                <th>81-90</th>
                                <th>Over 90</th>
                            </tr>
                        </thead>
                        <tbody>
                            {r.businessLines?.map((bl) => (
                                <tr key={bl.name}>
                                    <td className="cell-align-left">{bl.name}</td>
                                    <td><Link to={`/transactions?type=2&businessLineId=${bl.id || 0}&timePeriod=4`}>{bl.totalInFirstDateRangeForATM}</Link></td>
                                    <td><Link to={`/transactions?type=2&businessLineId=${bl.id || 0}&timePeriod=5`}>{bl.totalInSecondDateRangeForATM}</Link></td>
                                    <td><Link to={`/transactions?type=2&businessLineId=${bl.id || 0}&timePeriod=6`}>{bl.totalInThirdDateRangeForATM}</Link></td>
                                    <td><Link to={`/transactions?type=3&businessLineId=${bl.id || 0}&timePeriod=1`}>{bl.totalInFirstDateRangeForPOS}</Link></td>
                                    <td><Link to={`/transactions?type=3&businessLineId=${bl.id || 0}&timePeriod=2`}>{bl.totalInSecondDateRangeForPOS}</Link></td>
                                    <td><Link to={`/transactions?type=3&businessLineId=${bl.id || 0}&timePeriod=3`}>{bl.totalInThirdDateRangeForPOS}</Link></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}
            {!r && <div className="card-dash-empty">No data available</div>}
        </>
    )
}

// ========== Aging Transaction Card (120) ==========
/** Converted from agingTransaction120.tpl.html + agingTransaction120Controller.js */
function AgingTransaction120Card() {
    const { report, isLoading } = useAgingTransaction120()

    if (isLoading) return <div className="card-loading">Loading...</div>

    const r = report as {
        total?: number; businessLines?: Array<{
            name: string; id?: number
            totalInFirstDateRangeFor120?: number; totalInSecondDateRangeFor120?: number
            totalInThirdDateRangeFor120?: number; totalInFourthDateRangeFor120?: number
        }>
    } | null

    return (
        <>
            <div className="card-hd">
                <div className="card-title">Aging Transactions (120)</div>
            </div>
            {r && (
                <>
                    <section className="card-section">
                        <div className="dashboard-info-group dashboard-info-center">
                            <div className="dashboard-info-data">{r.total}</div>
                            <div className="dashboard-info-title">Total Transactions</div>
                        </div>
                    </section>
                    <table className="table table-sm table-align-right table-valign-middle">
                        <thead>
                            <tr className="secondary-header">
                                <th></th>
                                <th>&lt; 60</th>
                                <th>61 - 110</th>
                                <th>111 - 120</th>
                                <th>Over 120</th>
                            </tr>
                        </thead>
                        <tbody>
                            {r.businessLines?.map((bl) => (
                                <tr key={bl.name}>
                                    <td className="cell-align-left">{bl.name}</td>
                                    <td><Link to={`/transactions?type=4&businessLineId=${bl.id || 0}&timePeriod=7`}>{bl.totalInFirstDateRangeFor120}</Link></td>
                                    <td><Link to={`/transactions?type=4&businessLineId=${bl.id || 0}&timePeriod=8`}>{bl.totalInSecondDateRangeFor120}</Link></td>
                                    <td><Link to={`/transactions?type=4&businessLineId=${bl.id || 0}&timePeriod=9`}>{bl.totalInThirdDateRangeFor120}</Link></td>
                                    <td><Link to={`/transactions?type=4&businessLineId=${bl.id || 0}&timePeriod=10`}>{bl.totalInFourthDateRangeFor120}</Link></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}
            {!r && <div className="card-dash-empty">No data available</div>}
        </>
    )
}

// ========== Provisional Credit Card ==========
/** Converted from provisionalCreditRequired.tpl.html + provisionalCreditRequiredController.js */
function ProvisionalCreditCard() {
    const { report, isLoading } = useProvisionalCreditRequired()

    if (isLoading) return <div className="card-loading">Loading...</div>

    const r = report as {
        total?: number; businessLines?: Array<{
            name: string; id?: number
            totalInFirstDateRange?: number; totalInSecondDateRange?: number; totalInThirdDateRange?: number
        }>
    } | null

    return (
        <>
            <div className="card-hd">
                <div className="card-title">
                    Provisional Credit Required
                    <div className="card-subtitle">(Written Confirmation Received)</div>
                </div>
            </div>
            {r && (
                <>
                    <section className="card-section">
                        <div className="dashboard-info-group dashboard-info-center">
                            <div className="dashboard-info-data">{r.total}</div>
                            <div className="dashboard-info-title">Pending Transactions</div>
                        </div>
                    </section>
                    <table className="table table-sm table-align-right table-valign-middle">
                        <thead>
                            <tr className="secondary-header">
                                <th></th>
                                <th>&lt; 8 days</th>
                                <th>8-10 days</th>
                                <th>Over 10 days</th>
                            </tr>
                        </thead>
                        <tbody>
                            {r.businessLines?.map((bl) => (
                                <tr key={bl.name}>
                                    <td className="cell-align-left">{bl.name}</td>
                                    <td><Link to={`/transactions?type=1&businessLineId=${bl.id || 0}&timePeriod=1`}>{bl.totalInFirstDateRange}</Link></td>
                                    <td><Link to={`/transactions?type=1&businessLineId=${bl.id || 0}&timePeriod=2`}>{bl.totalInSecondDateRange}</Link></td>
                                    <td><Link to={`/transactions?type=1&businessLineId=${bl.id || 0}&timePeriod=3`}>{bl.totalInThirdDateRange}</Link></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}
            {!r && <div className="card-dash-empty">No data available</div>}
        </>
    )
}

// ========== Disputes Dashboard (4-quadrant layout) ==========
/** Converted from disputes.tpl.html */
export function DisputesDashboardPage() {
    const { hasAbility } = useAbility()

    return (
        <div className="page-content">
            <div className="page-header-container">
                <div className="page-header">
                    <h1 className="page-header-selection">Disputes Dashboard</h1>
                </div>
            </div>
            <div className="page-content-bd">
                <div className="dashboard-container dashboard-container-quadrants">
                    {hasAbility('ViewActiveTransactionsDashboard') && (
                        <div className="card card-dash card-dash-default">
                            <ActiveTransactionCard />
                        </div>
                    )}
                    {hasAbility('ViewProvisionalCreditRequiredDashboard') && (
                        <div className="card card-dash card-dash-default">
                            <ProvisionalCreditCard />
                        </div>
                    )}
                    {hasAbility('ViewAgingTransactionsDashboard') && (
                        <div className="card card-dash card-dash-default">
                            <AgingTransactionCard />
                        </div>
                    )}
                    {hasAbility('ViewAgingTransactionsDashboard') && (
                        <div className="card card-dash card-dash-default">
                            <AgingTransaction120Card />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

// ========== Dispute Team Performance ==========
interface TeamData {
    name: string
    totalCases: number
    unworkedCases: number
    inProgressCases: number
    provisionalCreditDeadlineWithinTwoDays: number
    provisionalCreditDeadlineWithinOneDay: number
    provisionalCreditDeadlineOverdue: number
    caseDeadlineWithinTwoDays: number
    caseDeadlineWithinOneDay: number
    caseDeadlineOverdue: number
    totalDollarAmount: number
}

/** Converted from disputeTeamPerformance.tpl.html + disputeTeamPerformanceController.js */
export function DisputeTeamPerformancePage() {
    const { teams, isLoading } = useDisputeTeamPerformance()
    const typedTeams = teams as TeamData[] | null

    return (
        <div className="page-content">
            <div className="page-header-container">
                <div className="page-header">
                    <h1 className="page-header-selection">Dispute Team Performance</h1>
                </div>
            </div>
            <div className="page-content-bd">
                <table className="table table-lines" id="table_dashboard_teamPerformance" style={{ display: !isLoading && (!typedTeams || typedTeams.length === 0) ? 'none' : undefined }}>
                    <thead>
                        <tr className="thead-top-level-labels">
                            <th></th>
                            <th colSpan={3} className="cell-align-right" style={{ textTransform: 'none' }}>Status</th>
                            <th colSpan={3} className="cell-align-right" style={{ textTransform: 'none' }}>PC Deadline</th>
                            <th colSpan={3} className="cell-align-right" style={{ textTransform: 'none' }}>Case Deadline</th>
                            <th className="cell-align-right" style={{ textTransform: 'none' }}>$ Amount</th>
                        </tr>
                        <tr className="has-table-hd-panel">
                            <th>Dispute Team</th>
                            <th className="cell-align-right">Total Cases</th>
                            <th className="cell-align-right">Unworked</th>
                            <th className="cell-align-right">In Progress</th>
                            <th className="cell-align-right">&lt; 2 days</th>
                            <th className="cell-align-right">&lt; 1 day</th>
                            <th className="cell-align-right">Overdue</th>
                            <th className="cell-align-right">&lt; 2 days</th>
                            <th className="cell-align-right">&lt; 1 day</th>
                            <th className="cell-align-right">Overdue</th>
                            <th className="cell-align-right">All Cases Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading && (
                            <tr><td colSpan={11} className="text-center"><LoadingIndicator /></td></tr>
                        )}
                        {!isLoading && typedTeams && typedTeams.map((team) => (
                            <tr key={team.name}>
                                <td>{team.name}</td>
                                <td className="cell-align-right">{team.totalCases}</td>
                                <td className="cell-align-right">{team.unworkedCases}</td>
                                <td className="cell-align-right">{team.inProgressCases}</td>
                                <td className="cell-align-right">{team.provisionalCreditDeadlineWithinTwoDays}</td>
                                <td className="cell-align-right">{team.provisionalCreditDeadlineWithinOneDay}</td>
                                <td className="cell-align-right">{team.provisionalCreditDeadlineOverdue}</td>
                                <td className="cell-align-right">{team.caseDeadlineWithinTwoDays}</td>
                                <td className="cell-align-right">{team.caseDeadlineWithinOneDay}</td>
                                <td className="cell-align-right">{team.caseDeadlineOverdue}</td>
                                <td className="cell-align-right">{team.totalDollarAmount != null ? team.totalDollarAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {!isLoading && (!typedTeams || typedTeams.length === 0) && (
                    <div className="text-center" style={{ padding: '40px 0', color: '#999', fontSize: '1.2em' }}>
                        No dispute teams are associated with your client.
                    </div>
                )}
            </div>
        </div>
    )
}
